/**
 * THE body the hair detector reads (ADR-039 T1) — one function, so the app's
 * Hair card and its door test (app/src/hair/hair-door.test.ts) cannot measure
 * two different bodies (LAWS: a fixture must walk the app's own door).
 *
 *  - the arrays are `tailDetectArrays`: the run's subject with the skin AS THE
 *    FILE PAINTED IT — the generator's weights are exactly what hair confirmation
 *    discards, and what the probe measured;
 *  - the extra bones are end nodes the skin does not list (Mixamo's
 *    `HeadTop_End`, Meshy's `head_end`), read off the scene graph — they CAP the
 *    skull's crown and never anchor it (probe brief, "No eye bones" point 2).
 *    Read in BIND-world like the arrays (Codex P2, #232): the end node's offset
 *    from its nearest skinned ancestor, carried through that ancestor's bind
 *    (inverse of its inverse bind matrix) — never the live pose, so the crown
 *    cap does not depend on the animation frame the card was opened on.
 *
 * Depends on: three, tail-arrays (adapter-three), kernel types. Depended on by:
 * main (the Hair tool's measurement) and the hair door test.
 */
import { Matrix4, SkinnedMesh, Vector3, type Object3D } from 'three';
import { measureHair, type HairBone, type HairReading, type HairRefusal, type SkullDeclarationShape } from '@harryhausen/kernel';
import type { SubjectArrays } from '@harryhausen/adapter-three';
import { tailDetectArrays } from './tail-arrays.js';

export interface HairSubject {
  readonly arrays: SubjectArrays;
  readonly extraBones: readonly HairBone[];
}

export function hairSubject(scene: Object3D): HairSubject {
  const arrays = tailDetectArrays(scene);
  return { arrays, extraBones: crownHelpers(scene, new Set(arrays.bones.map((b) => b.name))) };
}

/** The unskinned head-end nodes, in bind-world (exported for its pose-independence test). */
export function crownHelpers(scene: Object3D, skinned: ReadonlySet<string>): HairBone[] {
  scene.updateMatrixWorld(true);
  // each skinned bone's bind-world matrix; the first skin to list a bone wins (convert-rig's rule)
  const bindOf = new Map<Object3D, Matrix4>();
  scene.traverse((o) => {
    if (!(o instanceof SkinnedMesh)) return;
    o.skeleton.bones.forEach((b, i) => {
      const inverse = o.skeleton.boneInverses[i];
      if (!bindOf.has(b) && inverse !== undefined) bindOf.set(b, inverse.clone().invert());
    });
  });
  const bindWorld = (o: Object3D): Vector3 => {
    let anchor = o.parent;
    while (anchor !== null && !bindOf.has(anchor)) anchor = anchor.parent;
    // no skinned ancestor: nothing animates it relative to the skin, so the scene transform is the bind
    if (anchor === null) return new Vector3().setFromMatrixPosition(o.matrixWorld);
    const offset = anchor.matrixWorld.clone().invert().multiply(o.matrixWorld);
    return new Vector3().setFromMatrixPosition((bindOf.get(anchor) as Matrix4).clone().multiply(offset));
  };
  const extraBones: HairBone[] = [];
  scene.traverse((o) => {
    if (!skinned.has(o.name) && /head_?top|head_?end/i.test(o.name)) {
      const p = bindWorld(o);
      extraBones.push({ name: o.name, parent: o.parent?.name ?? null, worldPosition: { x: p.x, y: p.y, z: p.z } });
    }
  });
  return extraBones;
}

/** Measure the hair on a subject in the body's measured frame, with a person's skull when there is one. */
export function readHair(
  subject: HairSubject,
  frame: { readonly height: number; readonly forward: { readonly x: number; readonly z: number } },
  skull?: SkullDeclarationShape,
): HairReading | HairRefusal {
  return measureHair({
    positions: subject.arrays.positions,
    indices: subject.arrays.indices,
    skin: subject.arrays.skin,
    bones: subject.arrays.bones,
    extraBones: subject.extraBones,
    frame,
    ...(skull === undefined ? {} : { skull }),
  });
}
